import { useMemo, useState, type CSSProperties } from "react";
import { MAX_MINT_QUANTITY, TOTAL_SUPPLY_CAP } from "../utils/config";

const FEE_RATE = 0.01;

function formatEth(value: number) {
  if (!Number.isFinite(value)) return "0.0000";
  if (value >= 100) return value.toFixed(2);
  if (value >= 1) return value.toFixed(4);
  return value.toFixed(6);
}

export default function EarningsSimulator() {
  const [dailyVolume, setDailyVolume] = useState(1000);
  const [holdDays, setHoldDays] = useState(30);
  const [heldNfts, setHeldNfts] = useState(MAX_MINT_QUANTITY);
  const [activeNfts, setActiveNfts] = useState(Math.min(2000, TOTAL_SUPPLY_CAP));

  const estimatedWeth = useMemo(() => {
    const safeActive = Math.max(1, activeNfts);
    return dailyVolume * FEE_RATE * holdDays * Math.max(0, heldNfts) / safeActive;
  }, [activeNfts, dailyVolume, heldNfts, holdDays]);

  return (
    <section className="earnings-sim card" aria-labelledby="earnings-sim-title">
      <div>
        <div className="badge badge-green" style={{ marginBottom: "var(--space-3)" }}>Holder math</div>
        <h2 id="earnings-sim-title" className="text-3xl">Estimate the <span translate="no">WETH</span> stream</h2>
        <p className="text-muted" style={{ marginTop: "var(--space-3)", lineHeight: 1.7 }}>
          This is simple client-side math: daily pool volume times the 1% holder fee, split across active NFTs. It is not a promise of volume, price, or return.
        </p>
      </div>
      <div className="earnings-sim__panel">
        <RangeControl
          label="Daily volume"
          value={dailyVolume}
          min={1}
          max={2000}
          step={10}
          suffix="ETH"
          onValue={setDailyVolume}
        />
        <RangeControl
          label="Hold period"
          value={holdDays}
          min={1}
          max={365}
          suffix="days"
          onValue={setHoldDays}
        />
        <RangeControl
          label="Your NFTs"
          value={heldNfts}
          min={1}
          max={MAX_MINT_QUANTITY}
          onValue={setHeldNfts}
        />
        <RangeControl
          label="Active NFTs"
          value={activeNfts}
          min={1}
          max={TOTAL_SUPPLY_CAP}
          step={25}
          formatValue={(value) => value.toLocaleString()}
          onValue={setActiveNfts}
        />
      </div>
      <div className="earnings-sim__result">
        <span>Estimated <span translate="no">WETH</span></span>
        <strong><b>{formatEth(estimatedWeth)}</b> <em translate="no">WETH</em></strong>
        <small>Formula: volume x 1% x days x your NFTs / active NFTs.</small>
        <small className="sim-note">This is fee accrual on a bearer instrument, not staking yield. The instrument can be sold any time and the entitlement transfers with it.</small>
      </div>
    </section>
  );
}

function RangeControl({
  label,
  value,
  min,
  max,
  step = 1,
  suffix,
  formatValue,
  onValue
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  suffix?: string;
  formatValue?: (value: number) => string;
  onValue: (value: number) => void;
}) {
  const displayValue = `${formatValue ? formatValue(value) : value}${suffix ? ` ${suffix}` : ""}`;
  const handleValue = (nextValue: string) => onValue(Number(nextValue));

  return (
    <label className="range-control">
      <span className="range-control__head">
        <span>{label}</span>
      </span>
      <span className="range-control__track" style={rangeStyle(value, min, max)}>
        <output className="range-control__floating-value notranslate" translate="no" aria-live="polite">
          {displayValue}
        </output>
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          aria-label={label}
          aria-valuetext={displayValue}
          onInput={(event) => handleValue(event.currentTarget.value)}
          onChange={(event) => handleValue(event.currentTarget.value)}
        />
      </span>
    </label>
  );
}

function rangeStyle(value: number, min: number, max: number): CSSProperties {
  return {
    "--range-progress": `${Math.max(0, Math.min(100, ((value - min) / (max - min)) * 100))}%`
  } as CSSProperties;
}
